import { z } from 'zod';
import { agentChangesSchema } from './agent-change-schemas';
import { actionMessageSchema, agentCheckIssueSchema, agentIdSchema, optionalAgentIdSchema } from './agent-tool-schemas';
import { actionParameterSchema } from './agent-draft/fields';
import { workflowStepsSchema } from './agent-draft/workflow-steps';
import { runStepSchema } from './workflow-runs';
// Tools for building the workflows behind an agent: its actions (workflows the agent calls mid-conversation) and
// its post-call workflow. Workflows are read and written as nested steps; see workflow-steps.ts.
const workflowKindSchema = z
    .enum(['action', 'postCall', 'emailAction'])
    .describe("action: a workflow a call or chat agent calls mid-conversation. postCall: the workflow that runs after it ends. emailAction: an email agent's action.");
/** The node catalog's context for each kind of workflow the tools build. */
export const WORKFLOW_KIND_CONTEXTS = {
    action: 'toolCall',
    postCall: 'postCall',
    emailAction: 'actionStudio'
};
export const AGENT_ACTION_NAME_PATTERN = /^[A-Za-z0-9_]{1,64}$/;
const actionNameSchema = z
    .string()
    .regex(AGENT_ACTION_NAME_PATTERN)
    .describe('snake_case, such as lookup_policy: letters, digits and underscores, unique on the agent');
const actionSettingsSchema = z.object({
    name: actionNameSchema,
    description: z
        .string()
        .min(1)
        .describe('What it does and when to call it; the agent reads this to decide when to use it'),
    message: actionMessageSchema.optional().describe('Defaults to default'),
    variables: z
        .record(z.string(), actionParameterSchema)
        .optional()
        .describe('What the agent passes in when it calls the action, available to steps as {{action.name}}')
});
const stepsSchema = workflowStepsSchema.describe('The steps that run, in order. Call list_workflow_nodes and get_workflow_node first to know what to use.');
const issuesSchema = z
    .array(agentCheckIssueSchema)
    .describe('Problems in what was written; fix every error before saving');
export const listWorkflowNodesToolSchema = {
    name: 'list_workflow_nodes',
    description: 'List the steps you can use in a kind of workflow: each app and action, its version, what it does, and whether it ends the workflow.',
    inputSchema: z.object({ workflow: workflowKindSchema }),
    outputSchema: z.object({
        nodes: z.array(z.object({
            app: z.string(),
            action: z.string(),
            version: z.number(),
            name: z.string(),
            description: z.string(),
            endsWorkflow: z.boolean().describe('Nothing can come after it')
        }))
    })
};
export const getWorkflowNodeToolSchema = {
    name: 'get_workflow_node',
    description: "Describe one kind of step: the settings its config takes (as JSON Schema) and, where known, the outputs later steps can use as {{Step name.output}}. For settings that name a connection, use the connection's id from list_integrations.",
    inputSchema: z.object({
        app: z.string(),
        action: z.string(),
        version: z.number().optional(),
        workflow: workflowKindSchema
    }),
    outputSchema: z.object({
        app: z.string(),
        action: z.string(),
        version: z.number(),
        settings: z.record(z.string(), z.unknown()),
        outputs: z.record(z.string(), z.unknown()).optional()
    })
};
export const listIntegrationsToolSchema = {
    name: 'list_integrations',
    description: "List the integrations the organization has connected (CRMs, email, SMS, ticketing, webhooks and so on), with the id a step's connection setting takes.",
    inputSchema: z.object({}),
    outputSchema: z.object({
        integrations: z.array(z.object({
            app: z.string(),
            name: z.string(),
            detail: z.string().optional(),
            connectionId: z.string()
        }))
    })
};
export const getActionToolSchema = {
    name: 'get_action',
    description: "Read one of an agent's actions in full: its settings and every step of its workflow.",
    inputSchema: z.object({ agentId: optionalAgentIdSchema, actionId: z.string() }),
    outputSchema: z.object({
        actionId: z.string(),
        name: z.string(),
        description: z.string(),
        message: actionMessageSchema,
        variables: z.record(z.string(), actionParameterSchema),
        steps: z.array(z.record(z.string(), z.unknown()))
    })
};
export const createActionToolSchema = {
    name: 'create_action',
    description: "Add an action to an agent: something it can do mid-conversation by running a workflow. The agent only hears what the workflow's Return Response step sends back. It lands in the draft, and takes effect when the agent is saved and published. The editor then shows its workflow.",
    inputSchema: actionSettingsSchema.extend({ agentId: agentIdSchema, steps: stepsSchema }),
    outputSchema: z.object({
        agentId: z.string(),
        actionId: z.string(),
        name: z.string(),
        issues: issuesSchema,
        diff: agentChangesSchema
            .optional()
            .describe('What the edit changed, area by area, as its card shows it: the words it touched and the steps it added, changed or removed')
    })
};
export const updateActionToolSchema = {
    name: 'update_action',
    description: "Change one of an agent's actions. Only include what changes; steps replaces the whole workflow, so read it with get_action first.",
    inputSchema: actionSettingsSchema.partial().extend({
        agentId: agentIdSchema,
        actionId: z.string(),
        steps: stepsSchema.optional()
    }),
    outputSchema: z.object({
        agentId: z.string(),
        actionId: z.string(),
        name: z.string(),
        issues: issuesSchema,
        diff: agentChangesSchema
            .optional()
            .describe('What the edit changed, area by area, as its card shows it: the words it touched and the steps it added, changed or removed')
    })
};
export const removeActionToolSchema = {
    name: 'remove_action',
    description: 'Remove an action from an agent. Takes effect when the agent is saved and published. Also remove every mention of it from the flow.',
    inputSchema: z.object({ agentId: agentIdSchema, actionId: z.string() }),
    outputSchema: z.object({
        agentId: z.string(),
        actionId: z.string(),
        name: z.string(),
        diff: agentChangesSchema
            .optional()
            .describe('What the edit changed, area by area, as its card shows it: the words it touched and the steps it added, changed or removed')
    })
};
export const getPostCallWorkflowToolSchema = {
    name: 'get_post_call_workflow',
    description: "Read an agent's post-call workflow: whether it is turned on, and its steps.",
    inputSchema: z.object({ agentId: optionalAgentIdSchema }),
    outputSchema: z.object({
        agentId: z.string(),
        enabled: z.boolean(),
        steps: z.array(z.record(z.string(), z.unknown()))
    })
};
export const setPostCallWorkflowToolSchema = {
    name: 'set_post_call_workflow',
    description: 'Replace the post-call workflow of the agent open in the editor, which runs once after each conversation ends. The steps land in the draft and take effect when the agent is published. `enabled` turns post-call workflows on or off straight away, outside the draft; leave it out to keep it as it is.',
    inputSchema: z.object({ agentId: agentIdSchema, steps: stepsSchema, enabled: z.boolean().optional() }),
    outputSchema: z.object({
        agentId: z.string(),
        enabled: z.boolean(),
        issues: issuesSchema,
        diff: agentChangesSchema
            .optional()
            .describe('What the edit changed, area by area, as its card shows it: the words it touched and the steps it added, changed or removed')
    })
};
export const testWorkflowToolSchema = {
    name: 'test_workflow',
    description: "Run an action's workflow, or the post-call workflow, once with test data and report what each step did, the way get_workflow_run reports a past run. Steps really run: webhooks, emails, SMS and CRM updates happen for real (call controls are simulated), so the user confirms first. Tests the draft as it is now. Secret values show as [secret NAME]: don't guess them.",
    inputSchema: z.object({
        agentId: agentIdSchema,
        target: z.string().describe('An action id, or "post-call"'),
        testData: z
            .record(z.string(), z.unknown())
            .describe('What the workflow receives, by namespace: for an action, {"action": {…its variables}, "call": {…}}; for post-call, {"call": {"analysis": {…}, "transcript": …}}'),
        summary: z.string().describe('What the test will do, including any real side effects, shown to the user')
    }),
    outputSchema: z.object({
        completed: z.boolean().describe('false when the test was still running after a minute'),
        steps: z.array(runStepSchema)
    })
};
//# sourceMappingURL=workflow-tool-schemas.js.map